import axios from 'axios';
import { WorkoutData, SleepMetrics, RecoveryMetrics } from '../types';

const stravaApi = axios.create({
  baseURL: 'https://www.strava.com/api/v3',
});

interface StravaActivity {
  id: number;
  moving_time: number;
  average_heartrate?: number;
  distance?: number;
  type: string;
  start_date: string;
}

// Oura API v2 response shapes (only the fields we use)
interface OuraDailySleep {
  score: number;
  timestamp: string;
}

interface OuraDailyReadiness {
  score: number;
  temperature_deviation: number | null;
  timestamp: string;
}

interface OuraSleepPeriod {
  day: string;
  total_sleep_duration: number; // seconds
  average_hrv: number | null;   // ms
  lowest_heart_rate: number | null; // bpm
}

export const fetchStravaWorkouts = async (token: string): Promise<WorkoutData[]> => {
  try {
    if (!token) return [];

    const response = await stravaApi.get('/athlete/activities', {
      headers: { Authorization: `Bearer ${token}` },
      params: {
        after: Math.floor((Date.now() - 7 * 86400000) / 1000), // Last 7 days
        per_page: 50,
      },
    });

    return response.data.map((activity: StravaActivity) => ({
      id: String(activity.id),
      duration: activity.moving_time,
      intensity: calculateIntensity(activity.average_heartrate),
      type: activity.type,
      timestamp: activity.start_date,
      distance: activity.distance,
      averageHeartRate: activity.average_heartrate,
    }));
  } catch (error) {
    // Never substitute fake workouts — an empty week is an honest answer,
    // invented sessions poison the coach's training-load picture.
    console.error('Error fetching Strava data:', error);
    return [];
  }
};

export const fetchOuraData = async (token: string): Promise<{
  sleep: SleepMetrics;
  recovery: RecoveryMetrics;
} | null> => {
  try {
    if (!token) return null;

    // The Oura API blocks browser calls (no CORS headers), so all reads go
    // through our oura-data proxy function.
    const response = await fetch('/.netlify/functions/oura-data', {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) {
      throw new Error(`Oura proxy error: ${response.status}`);
    }

    const payload: {
      daily_sleep: OuraDailySleep[];
      daily_readiness: OuraDailyReadiness[];
      sleep: OuraSleepPeriod[];
    } = await response.json();

    const dailySleep = payload.daily_sleep[payload.daily_sleep.length - 1];
    const readiness = payload.daily_readiness[payload.daily_readiness.length - 1];

    // Use the most recent day's sleep; a day can have several periods
    // (naps), so take the longest one from that day.
    const latestDay = payload.sleep.reduce(
      (max, p) => (p.day > max ? p.day : max),
      payload.sleep[0]?.day ?? ''
    );
    const mainSleep = payload.sleep
      .filter(p => p.day === latestDay)
      .reduce<OuraSleepPeriod | undefined>(
        (longest, p) =>
          !longest || p.total_sleep_duration > longest.total_sleep_duration ? p : longest,
        undefined
      );

    return {
      sleep: {
        duration: mainSleep?.total_sleep_duration ?? 0,
        quality: dailySleep?.score ?? 0,
        readiness: readiness?.score ?? 0,
        timestamp: dailySleep?.timestamp ?? new Date().toISOString(),
      },
      recovery: {
        hrv: mainSleep?.average_hrv ?? 0,
        restingHeartRate: mainSleep?.lowest_heart_rate ?? 0,
        bodyTemperature: readiness?.temperature_deviation ?? 0,
        timestamp: readiness?.timestamp ?? new Date().toISOString(),
        readinessScore: readiness?.score,
      },
    };
  } catch (error) {
    // Never substitute fake metrics for a failed read: the dashboard
    // showed plausible-looking mock HRV/RHR for a day before anyone
    // noticed the Oura token had expired. Null lets the app fall back to
    // the next real source (or honestly say "sample data").
    console.error('Error fetching Oura data:', error);
    return null;
  }
};

const calculateIntensity = (avgHeartRate?: number): 'low' | 'moderate' | 'high' => {
  if (!avgHeartRate) return 'moderate';
  if (avgHeartRate < 120) return 'low';
  if (avgHeartRate < 150) return 'moderate';
  return 'high';
};